import React from "react";
import styled from "styled-components";
import { formatDate } from "./helpers";
import { getCulture } from "../../utils/Utils";
import placeholderImg from "../../assets/images/placeholder-img-rectangle.svg";
import { colors, media } from "./mixins";

export type ProducerEventGroup = {
  cmsNodeId: number;
  eventGroupId: number;
  eventGroupCode: string;
  categoryId: number;
  additionalCategories: null;
  subCategories: null;
  tags: null;
  title: string;
  name: string;
  smallDescription: string | null;
  createdDate: string;
  nextEventDate: string;
  lastEventDate: string;
  venueId: number;
  venueName: string | null;
  imageUrl: string | null;
  mobileImageUrl: string | null;
  isRecentlyAdded: false;
  redirectUrl: string | null;
  ticketPrice: string;
  occupancy: number;
  popularity: number;
  isFeatured: boolean;
  featuredPriority: number;
  ticketsSold: number;
  isSeasonTicket: boolean;
};

export type EventProps = ProducerEventGroup;

export type EventCardLabels = {
  soldOut: string;
};

type StatusLabel = { text: string; bg: string; color: string };

function getStatusLabel(
  occupancy: number,
  labels: EventCardLabels,
): StatusLabel | null {
  if (occupancy >= 100) {
    return { text: labels.soldOut, bg: "#E9E8EC", color: "#696781" };
  }
  return null;
}

type EventCardProps = ProducerEventGroup & {
  dateLabel?: string;
  labels?: EventCardLabels;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

const Card = styled.a`
  display: flex;
  flex-direction: column;
  width: 100%;
  // height: 300px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3.84px 7.679px 0 rgba(0, 0, 0, 0.16);
  text-decoration: none;
  background: #fff;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    text-decoration: none;
    transform: translateY(-4px);
    box-shadow: 0 8px 20px 0 rgba(0, 0, 0, 0.2);
  }

  ${media.below(633)} {
    width: 100%;
  }
`;

const CardImage = styled.aside`
  width: 100%;
  overflow: hidden;
  background-color: #edf1f6;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const CardInfo = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardDate = styled.time`
  color: ${colors.dateColor};
  font-size: 14px;
  font-weight: 600;
`;

const CardNameWrapper = styled.div`
  height: 44px;
`;

const CardName = styled.h3`
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
`;

const CardVenue = styled.p`
  margin: 0;
  margin-top: auto;
  padding-top: 8px;
  color: #646567;
  font-size: 14px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  line-height: 15px;
`;

const BadgeSlot = styled.div`
  height: 28px;
  display: flex;
  align-items: center;
`;

const StatusBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  align-self: flex-start;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.08);
`;

export const EventCard = ({
  name,
  nextEventDate,
  imageUrl,
  redirectUrl,
  venueName,
  dateLabel,
  occupancy,
  labels,
  onClick,
}: EventCardProps) => {
  const date =
    dateLabel ??
    formatDate({
      date: nextEventDate,
      culture: getCulture(),
      hideTime: true,
      hasWeekday: false,
    });

  const statusLabel = labels ? getStatusLabel(occupancy, labels) : null;

  return (
    <Card href={redirectUrl ?? undefined} onClick={onClick}>
      <CardImage>
        <img
          src={
            imageUrl ? `/getattachment/${imageUrl}/image.png` : placeholderImg
          }
          alt={name}
        />
      </CardImage>
      <CardInfo>
        <CardDate>{date}</CardDate>
        <CardNameWrapper>
          <CardName>{name}</CardName>
        </CardNameWrapper>

        <BadgeSlot>
          {statusLabel && (
            <StatusBadge $bg={statusLabel.bg} $color={statusLabel.color}>
              {statusLabel.text}
            </StatusBadge>
          )}
        </BadgeSlot>
        <CardVenue>{venueName}</CardVenue>
      </CardInfo>
    </Card>
  );
};
